import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Anchor, Box, Group, Stack } from '@mantine/core'
import { AppBadge, AppButton, AppTabs, AppText } from '../../app/common/ui'
import { format } from 'date-fns'
import agent from '../../app/api/agent'
import { EmptyState, Page } from '../../app/common/ui'
import LoadingComponent from '../../app/layout/LoadingComponents'
import type { AccountActionDto } from '../../app/models/accountAction'
import type { LegalAcceptanceDto } from '../../app/models/legalAcceptance'
import { useStore } from '../../app/stores/store'
import AppealComingSoonModal from './AppealComingSoonModal'
import DeleteAccountModal from './DeleteAccountModal'

const ACTION_COLORS: Record<string, string> = {
  Warn: 'yellow',
  Remove: 'red',
  Escalate: 'orange',
  Suspend: 'red'
}

export default observer(function SettingsPage () {
  const history = useHistory()
  const { userStore } = useStore()
  const username = userStore.user?.username

  const [activeTab, setActiveTab] = useState<string | null>('account')
  const [accountActions, setAccountActions] = useState<AccountActionDto[]>([])
  const [legalAcceptances, setLegalAcceptances] = useState<LegalAcceptanceDto[]>([])
  const [loadingActions, setLoadingActions] = useState(true)
  const [loadingLegal, setLoadingLegal] = useState(true)
  const [appealOpen, setAppealOpen] = useState(false)
  const [appealSummary, setAppealSummary] = useState<string | undefined>()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!userStore.isLoggedIn) {
      history.push('/login')
      return
    }
    if (!username) return

    setLoadingActions(true)
    setLoadingLegal(true)
    void agent.Profiles.accountActions(username)
      .then(setAccountActions)
      .catch(() => setAccountActions([]))
      .finally(() => setLoadingActions(false))

    void agent.Profiles.legalAcceptances(username)
      .then(setLegalAcceptances)
      .catch(() => setLegalAcceptances([]))
      .finally(() => setLoadingLegal(false))
  }, [username, userStore.isLoggedIn, history])

  const openAppeal = (summary?: string) => {
    setAppealSummary(summary)
    setAppealOpen(true)
  }

  const handleExport = async () => {
    setExporting(true)
    setExportError(null)
    setExportSuccess(null)
    try {
      const { blob, filename } = await agent.Account.exportMyData()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setExportSuccess('Export downloaded successfully.')
    } catch (error: any) {
      setExportError(error?.response?.data?.detail ?? 'Failed to export data. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  if (!userStore.isLoggedIn || !username) {
    return <LoadingComponent content="Loading settings..." />
  }

  return (
    <Page title="Settings">
      <Box sx={{ maxWidth: 720, margin: '0 auto' }}>
        <AppText size="sm" color="dimmed" mb="lg">
          Review moderation actions, privacy controls, and policies you have accepted.
        </AppText>

        <AppTabs value={activeTab} onTabChange={setActiveTab}>
          <AppTabs.List>
            <AppTabs.Tab value="account">Account actions</AppTabs.Tab>
            <AppTabs.Tab value="legal">Legal agreements</AppTabs.Tab>
            <AppTabs.Tab value="privacy">Privacy & data</AppTabs.Tab>
          </AppTabs.List>

          <AppTabs.Panel value="account" pt="md">
            {loadingActions
              ? <LoadingComponent content="Loading account actions..." />
              : accountActions.length === 0
                ? (
                <EmptyState
                  title="No moderation actions"
                  message="Warnings, removals, and suspensions will appear here when they apply to your account."
                />
                  )
                : (
                <Stack spacing="sm">
                  {accountActions.map((action, index) => (
                    <Box
                      key={`${action.occurredAt}-${action.action}-${index}`}
                      sx={{
                        padding: '14px 16px',
                        border: '1px solid var(--gs-border)',
                        borderRadius: 8,
                        background: 'var(--gs-surface, rgba(255,255,255,0.02))'
                      }}
                    >
                      <Group position="apart" mb={6} noWrap>
                        <Group spacing="xs">
                          <AppBadge color={ACTION_COLORS[action.action] ?? 'gray'} variant="filled" size="sm">
                            {action.action}
                          </AppBadge>
                          {action.violationLabel
                            ? (
                            <AppText size="xs" color="dimmed">
                              {action.violationLabel}
                              {action.sectionCitation ? ` · ${action.sectionCitation}` : ''}
                            </AppText>
                              )
                            : null}
                        </Group>
                        <AppText size="xs" color="dimmed">
                          {format(new Date(action.occurredAt), 'MMM d, yyyy')}
                        </AppText>
                      </Group>
                      <AppText size="sm" mb="sm" sx={{ lineHeight: 1.5 }}>
                        {action.summary}
                      </AppText>
                      <Group spacing="sm">
                        {action.policyPath
                          ? (
                          <Anchor component={Link} to={action.policyPath} size="xs">
                            View policy
                          </Anchor>
                            )
                          : null}
                        {action.canAppeal
                          ? (
                          <AppButton
                            variant="subtle"
                            size="xs"
                            compact
                            onClick={() => openAppeal(action.summary)}
                          >
                            Appeal
                          </AppButton>
                            )
                          : null}
                      </Group>
                    </Box>
                  ))}
                </Stack>
                  )}
          </AppTabs.Panel>

          <AppTabs.Panel value="legal" pt="md">
            {loadingLegal
              ? <LoadingComponent content="Loading agreements..." />
              : legalAcceptances.length === 0
                ? (
                <EmptyState
                  title="No recorded agreements"
                  message="Acceptances are recorded when you sign up, post to Slop, or accept upload policies."
                />
                  )
                : (
                <Stack spacing="sm">
                  {legalAcceptances.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        padding: '14px 16px',
                        border: '1px solid var(--gs-border)',
                        borderRadius: 8
                      }}
                    >
                      <Group position="apart" mb={4}>
                        <AppText weight={600} size="sm">
                          {row.policyName}
                        </AppText>
                        <AppText size="xs" color="dimmed">
                          v{row.acceptedVersion}
                        </AppText>
                      </Group>
                      <AppText size="xs" color="dimmed" mb={6}>
                        Accepted {format(new Date(row.acceptedAt), 'MMM d, yyyy h:mm a')}
                      </AppText>
                      {row.policyPath
                        ? (
                        <Anchor component={Link} to={row.policyPath} size="xs">
                          Read current policy
                        </Anchor>
                          )
                        : null}
                    </Box>
                  ))}
                </Stack>
                  )}
          </AppTabs.Panel>

          <AppTabs.Panel value="privacy" pt="md">
            <Stack spacing="sm">
              <Box
                sx={{
                  padding: '14px 16px',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8
                }}
              >
                <AppText weight={600} size="sm" mb={6}>
                  Download my data
                </AppText>
                <AppText size="sm" color="dimmed" mb="md" sx={{ lineHeight: 1.6 }}>
                  Get a machine-readable ZIP export of your profile, posts, comments, vault, wishlist,
                  legal acceptances, and account-action history.
                </AppText>
                <Group spacing="sm">
                  <AppButton variant="outline" loading={exporting} onClick={() => void handleExport()}>
                    Download export
                  </AppButton>
                  {exportSuccess && <AppText size="xs" color="green">{exportSuccess}</AppText>}
                  {exportError && <AppText size="xs" color="red">{exportError}</AppText>}
                </Group>
              </Box>
              <Box
                sx={{
                  padding: '14px 16px',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8
                }}
              >
                <AppText weight={600} size="sm" mb={6}>
                  Delete my account
                </AppText>
                <AppText size="sm" color="dimmed" mb="md" sx={{ lineHeight: 1.6 }}>
                  Permanently remove your account after email confirmation. See our{' '}
                  <Anchor component={Link} to="/privacy" size="sm">Privacy Policy</Anchor> for what is deleted,
                  retained, or anonymized.
                </AppText>
                <AppButton color="red" variant="outline" onClick={() => setDeleteModalOpen(true)}>
                  Delete my account
                </AppButton>
              </Box>
            </Stack>
          </AppTabs.Panel>
        </AppTabs>
      </Box>

      <DeleteAccountModal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} />

      <AppealComingSoonModal
        opened={appealOpen}
        onClose={() => setAppealOpen(false)}
        summary={appealSummary}
      />
    </Page>
  )
})
